"use client";

import React, { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CountryCode {
  en: string;
  cn: string;
  code: string;
}

interface CountryCodeSelectorProps {
  value?: string;
  onChange?: (code: string, country: CountryCode) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
  value = '',
  onChange,
  placeholder = "选择国家区号",
  className,
  disabled = false
}) => {
  const [countries, setCountries] = useState<CountryCode[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<CountryCode[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 加载国家数据
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await fetch('/country-code.json');
        const data: CountryCode[] = await response.json();
        setCountries(data);
        setFilteredCountries(data);
      } catch (error) {
        console.error('加载国家数据失败:', error);
      }
    };
    
    loadCountries();
  }, []);

  // 根据传入的 value 设置选中的国家
  useEffect(() => {
    if (value && countries.length > 0) {
      const country = countries.find(c => c.code === value || c.code === `+${value}` || c.code.replace('+', '') === value);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [value, countries]);

  // 搜索过滤
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCountries(countries);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = countries.filter(country => 
        country.en.toLowerCase().includes(query) ||
        country.cn.includes(query) ||
        country.code.includes(query)
      );
      setFilteredCountries(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchQuery, countries]);

  // 处理键盘导航
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCountries.length) {
          selectCountry(filteredCountries[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;
    }
  };

  // 选择国家
  const selectCountry = (country: CountryCode) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
    
    // 提取纯数字区号（去掉+号）
    const cleanCode = country.code.replace('+', '');
    onChange?.(cleanCode, country);
  };

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 滚动到高亮的选项
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-between h-10 px-3 text-left font-normal",
            !selectedCountry && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <span className="flex items-center space-x-2">
            {selectedCountry ? (
              <>
                <span className="text-sm font-medium">{selectedCountry.code}</span>
                <span className="text-xs text-gray-500">{selectedCountry.cn}</span>
              </>
            ) : (
              <span>{placeholder}</span>
            )}
          </span>
          <i className={cn(
            "fas fa-chevron-down text-xs transition-transform",
            isOpen && "transform rotate-180"
          )} />
        </Button>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60">
          {/* 搜索输入框 */}
          <div className="p-2 border-b">
            <Input
              ref={inputRef}
              type="text"
              placeholder="搜索国家或区号..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full text-sm"
              autoFocus
            />
          </div>

          {/* 国家列表 */}
          <div 
            ref={dropdownRef}
            className="max-h-48 overflow-y-auto"
          >
            {filteredCountries.length === 0 ? (
              <div className="p-2 text-sm text-gray-500 text-center">
                未找到匹配的国家
              </div>
            ) : (
              filteredCountries.map((country, index) => (
                <div
                  key={`${country.code}-${country.en}`}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 cursor-pointer text-sm hover:bg-gray-100",
                    index === highlightedIndex && "bg-blue-50",
                    selectedCountry?.code === country.code && "bg-blue-100"
                  )}
                  onClick={() => selectCountry(country)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-blue-600 min-w-[60px]">
                      {country.code}
                    </span>
                    <span className="text-gray-700">{country.cn}</span>
                  </div>
                  <span className="text-xs text-gray-400 truncate max-w-[100px]">
                    {country.en}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelector;
